import { StatusIndicator, type StatusTone } from '@neo/primitives/status-indicator'
import { Tooltip } from '@neo/primitives/tooltip'
import { Typography } from '@neo/primitives/typography'
import RenderFileIcon from '@neo/shared/components/file-icon'
import { TrashIcon } from 'lucide-react'
import type { RFileUploadResponse } from 'v2/types/api/auth'
import {
  TaskAssignmentStatus,
  TaskAssignmentStatusLabelMapping,
  TaskPriority,
  TaskPriorityLabelMapping,
  TaskStatus,
  TaskStatusLabelMapping,
} from './tasks.config'

interface TaskFilePreviewProps {
  attachment: RFileUploadResponse
  onDelete: (attachment: RFileUploadResponse) => void
  disabled?: boolean
}

export const TaskFilePreview = ({ attachment, onDelete, disabled }: TaskFilePreviewProps) => (
  <div className="flex w-48 items-center justify-between gap-1 rounded-md border border-border bg-background p-1.5">
    <button
      type="button"
      className="flex cursor-pointer items-center gap-1 overflow-hidden rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onClick={() => window.open(attachment.file_url, '_blank')}
    >
      <RenderFileIcon fileType={attachment.file_url.split('.').pop() || ''} />
      <Typography variant="XS/400" className="text-nowrap text-ellipsis overflow-hidden">
        {attachment.file_name}
      </Typography>
    </button>
    {!disabled && (
      <button
        type="button"
        className="rounded-sm border border-border p-1 text-tertiary transition-colors hover:bg-hv1 hover:text-status-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={() => onDelete(attachment)}
        data-testid="button-delete-attachment"
        aria-label={`Delete ${attachment.file_name}`}
      >
        <Tooltip content="Delete attachment">
          <TrashIcon height={12} width={12} />
        </Tooltip>
      </button>
    )}
  </div>
)

const getTaskPriorityTone = (type: TaskPriority): StatusTone => {
  switch (type) {
    case TaskPriority.medium:
      return 'progress'
    case TaskPriority.high:
      return 'warning'
    case TaskPriority.critical:
      return 'danger'
    default:
      return 'neutral'
  }
}

export const TaskPriorityIcon = ({ type }: { type: TaskPriority }) => {
  return (
    <StatusIndicator
      tone={getTaskPriorityTone(type)}
      aria-label={`${TaskPriorityLabelMapping[type]} priority`}
    >
      <span className="sr-only">{TaskPriorityLabelMapping[type]}</span>
    </StatusIndicator>
  )
}

export const TaskPriorityFlag = ({ type }: { type: TaskPriority }) => (
  <StatusIndicator tone={getTaskPriorityTone(type)}>
    {TaskPriorityLabelMapping[type]}
  </StatusIndicator>
)

export const TaskStatusFlag = ({ status }: { status: TaskStatus }) => {
  const label = TaskStatusLabelMapping[status]
  if (!label) return null

  const toneMap: Record<TaskStatus, StatusTone> = {
    [TaskStatus.todo]: 'neutral',
    [TaskStatus.inProgress]: 'progress',
    [TaskStatus.resolved]: 'success',
    [TaskStatus.waitingOnCustomer]: 'warning',
  }

  return <StatusIndicator tone={toneMap[status]}>{label}</StatusIndicator>
}

export const TaskAssignmentStatusFlag = ({ status }: { status: TaskAssignmentStatus }) => {
  const label = TaskAssignmentStatusLabelMapping[status]
  if (!label) return null

  const toneMap: Record<TaskAssignmentStatus, StatusTone> = {
    [TaskAssignmentStatus.pending]: 'warning',
    [TaskAssignmentStatus.accepted]: 'success',
    [TaskAssignmentStatus.declined]: 'danger',
    [TaskAssignmentStatus.failed]: 'danger',
    [TaskAssignmentStatus.scheduled]: 'progress',
  }

  return <StatusIndicator tone={toneMap[status]}>{label}</StatusIndicator>
}
